// Idempotency-Key generator.
//
// Контракт (зафиксирован в docs/rules/frontend_rules.md):
//   - UUID v4 генерируется ОДИН раз на каждое пользовательское submit-действие
//     (клик «Сгенерировать» / «Корректура»).
//   - Тот же ключ переиспользуется при автоматических axios-retry внутри одной mutation.
//   - При пользовательском retry (новый клик) — генерируется НОВЫЙ UUID.
//   - Cross-tool isolation: ключи не пересекаются между tools на backend'е (PRD §11.2).
//
// Реализация: используем строго криптоустойчивые источники.
//   1. `crypto.randomUUID()` (доступно в браузерах с 2022, в jsdom с v22+).
//   2. `crypto.getRandomValues()` + ручной UUID v4 (фолбэк для старых браузеров).
//   Если ни одного — throw, потому что Math.random() даст коллизии при ~24ч
//   окне идемпотентности backend'а и при общем секрете между пользователями
//   приведёт к утечке данных (idempotent_replay вернёт чужой task).
//
// Решение «лучше явная ошибка, чем тихий слабый UUID» зафиксировано на
// quality-аудите Этапа 2 BP002.

export class CryptoUnavailableError extends Error {
  constructor() {
    super(
      "Crypto API недоступен — невозможно сгенерировать криптоустойчивый Idempotency-Key. " +
        "Откройте приложение в современном браузере или через HTTPS.",
    );
    this.name = "CryptoUnavailableError";
  }
}

export function generateIdempotencyKey(): string {
  // 1. crypto.randomUUID — стандартный путь во всех современных браузерах
  //    и в jsdom v22+. Возвращает UUID v4.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  // 2. Фолбэк через getRandomValues — для очень старых окружений (HTTP без
  //    secure context, embedded WebViews и т.п.). Тоже криптоустойчиво.
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
    return (
      `${hex[0]}${hex[1]}${hex[2]}${hex[3]}-` +
      `${hex[4]}${hex[5]}-` +
      `${hex[6]}${hex[7]}-` +
      `${hex[8]}${hex[9]}-` +
      `${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`
    );
  }

  // 3. Никакого крипто-API → явная ошибка. Math.random() здесь использовать
  //    нельзя: при ~24ч окне идемпотентности backend'а возможна коллизия
  //    между двумя пользователями → один получит чужой task (security incident).
  throw new CryptoUnavailableError();
}
